import useBudgets from "@/store/use-budgets";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CaretRight } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { BudgetSummary } from "@/lib/types";
import { useMemo } from "react";
import { Mixpanel } from "@/lib/mixpanel";
import ProgressBar from "@/components/custom/progress-bar";
import { currencyFormatter } from "@/lib/utils";

interface SummaryCardProps {
  budgetSummaries: BudgetSummary[];
}

const SummaryCard = ({ budgetSummaries }: SummaryCardProps) => {
  const { currentBudget, setCurrentBudget } = useBudgets();
  const { t } = useTranslation();

  const vessels = useMemo(
    () => budgetSummaries?.map((summary) => summary.vesselName),
    [budgetSummaries]
  );

  const handleVesselChange = (value: string) => {
    const budget = budgetSummaries.find(
      (summary) => summary.vesselName === value
    );

    if (budget) {
      setCurrentBudget(budget);
      Mixpanel.track("Vessel Change on Summary Card");
    }
  };

  const goalBudget = useMemo(() => {
    return (
      ((currentBudget?.totalIncome ?? 0) / (currentBudget?.totalBudget ?? 0)) *
      100
    );
  }, [currentBudget]);

  const realizedBudget = useMemo(() => {
    return (
      ((currentBudget?.totalExpenses ?? 0) /
        (currentBudget?.totalBudget ?? 0)) *
      100
    );
  }, [currentBudget]);

  return (
    <div
      data-testid="summary-card"
      className="mt-4 flex flex-col items-center justify-center gap-4 px-5"
    >
      <div className="g-fu flex w-full flex-row items-center justify-center">
        <div className="w-full rounded-xl border border-[#4c717c40] shadow-[0px_1px_4px_0px_#00000025]">
          <div
            style={{
              background:
                "linear-gradient(135deg, rgba(0,0,0,.2) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0) 80%, rgba(106,228,252,.2) 100%)",
            }}
            className="h-full w-full rounded-xl p-[15px]"
          >
            <div className="flex flex-row items-center justify-between">
              <Select
                value={currentBudget?.vesselName}
                onValueChange={handleVesselChange}
              >
                <SelectTrigger
                  data-testid="budget-select"
                  size="sm"
                  className="w-fit pr-7"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {vessels.map((vessel) => (
                    <SelectItem
                      data-testid={`budget-select-item-${vessel}`}
                      key={vessel}
                      value={vessel}
                    >
                      {vessel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Link
                onClick={() => {
                  Mixpanel.track("Navigate to Budget Details");
                }}
                to="#"
                className="flex items-center justify-center gap-2"
              >
                <span className="text-sm text-white/50">
                  {t("button.details")}
                </span>
                <CaretRight color="white" weight="bold" size={16} />
              </Link>
            </div>
            <div className="mt-7">
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-row items-center justify-center gap-2">
                  <span className="flex h-[6px] min-h-[6px] w-[6px] min-w-[6px] rounded-full bg-[#26C2DD]" />
                  <span className="text-[13px] text-white">
                    {t("label.goal_budget")}
                  </span>
                </div>

                <span className="text-xl font-semibold leading-5 tracking-tight text-white">
                  {currencyFormatter(
                    currentBudget?.totalBudget ?? 0,
                    currentBudget?.defaultCurrencyCode ?? "USD"
                  )}
                </span>
              </div>
              <ProgressBar
                className="mt-3"
                barColor="#26C2DD"
                value={Math.min(goalBudget, 100)}
              />
            </div>

            <div className="mt-2">
              <ProgressBar
                className="mt-3"
                barColor="#33D78A"
                value={Math.min(realizedBudget, 100)}
              />
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex flex-row items-center justify-center gap-2">
                  <span className="flex h-[6px] min-h-[6px] w-[6px] min-w-[6px] rounded-full bg-[#33D78A]" />
                  <span className="text-[13px] text-white">
                    {t("label.realized_budget")}
                  </span>
                </div>

                <span className="text-xl font-semibold leading-5 tracking-tight text-white">
                  {currencyFormatter(
                    currentBudget?.totalExpenses ?? 0,
                    currentBudget?.defaultCurrencyCode ?? "USD"
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryCard;
